import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import StructuredData from "@/components/seo/StructuredData";
import { autoRunSEOChecks } from "@/lib/seo-qa";
import { useEffect } from "react";
import AppLayout from "@/components/layout/AppLayout";
import WhatsAppChat from "./components/WhatsAppChat";
import ConsentManager from "./components/consent/ConsentManager";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Services from "./pages/Services";
import ServiceAmazonRedirect from "./pages/ServiceAmazonRedirect";
import CreativeStudio from "./pages/CreativeStudio";
import Methode from "./pages/Methode";
import FAQ from "./pages/FAQ";
import Audit from "./pages/Audit";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import CaseBeauteCapillaire from "./pages/CaseBeauteCapillaire";
import CaseFoodExport from "./pages/CaseFoodExport";
import CaseTechLogistique from "./pages/CaseTechLogistique";
import Cases from "./pages/Cases";
import CasesAmazon from "./pages/CasesAmazon";
import CaseAmazonVolume from "./pages/CaseAmazonVolume";
import CaseAmazonRevenue from "./pages/CaseAmazonRevenue";
import CasesCreative from "./pages/CasesCreative";
import IndustrielsFabricants from "./pages/IndustrielsFabricants";
import MarquesDnvb from "./pages/MarquesDnvb";
import IA from "./pages/IA";
import IAAmazonAds from "./pages/IAAmazonAds";
import IASeOListingsAPlus from "./pages/IASeOListingsAPlus";
import IACatalogHealthBuyBox from "./pages/IACatalogHealthBuyBox";
import IAPrevisionDemandeStocks from "./pages/IAPrevisionDemandeStocks";
import IAAgentsAssistantsAmazon from "./pages/IAAgentsAssistantsAmazon";
import IACasClientsIA from "./pages/IACasClientsIA";
import IAAudit from "./pages/IAAudit";
import ServicesBuyboxSprint from "./pages/ServicesBuyboxSprint";
import IAAgentsAssistantsAmazonImpl from "./pages/IAAgentsAssistantsAmazonImpl";
import ServicesAmazon from "./pages/ServicesAmazon";
import AuteursCosmosWave from "./pages/AuteursCosmosWave";
import SaveCredentials from "./pages/SaveCredentials";
import Conformite from "./pages/Conformite";
import Comparatifs from "./pages/Comparatifs";
import ComparatifsAlternatives from "./pages/ComparatifsAlternatives";
import ComparatifsVs from "./pages/ComparatifsVs";
import ComparatifsTopAlternatives from "./pages/ComparatifsTopAlternatives";
import AccompagnementAmazon from "./pages/AccompagnementAmazon";
import LLMFacts from "./pages/LLMFacts";
import Login from "./pages/Login";
import EspaceClient from "./pages/EspaceClient";
import Dashboard from "./pages/Dashboard";
import Admin from "./pages/Admin";
import Welcome from "./pages/Welcome";
import Logout from "./pages/Logout";
import ClientLogin from "./pages/clients/Login";
import LegacyClientDashboard from "./pages/clients/Dashboard";
import ClientDashboard from "./pages/ClientDashboard";
import ClientSettings from "./pages/ClientSettings";
import AuditGratuit from "./pages/AuditGratuit";
import AdminAuth from "./pages/AdminAuth";
import AdminCXLabHome from "./pages/AdminCXLabHome";
import AdminSimulateurs from "./pages/AdminSimulateurs";
import AdminPlaybooks from "./pages/AdminPlaybooks";
import AdminPlaybookAmazonAds from "./pages/AdminPlaybookAmazonAds";
import AdminSimulateurAmzCosmDz from "./pages/AdminSimulateurAmzCosmDz";
import AdminSimulateurAmzCosmFr from "./pages/AdminSimulateurAmzCosmFr";
import AdminSimulateurAmzAds from "./pages/AdminSimulateurAmzAds";
import AdminSimulateurMargesPhysique from "./pages/AdminSimulateurMargesPhysique";
import FixCredentials from "./pages/FixCredentials";

const queryClient = new QueryClient();

const App = () => {
  // Auto-run SEO checks in development
  useEffect(() => {
    autoRunSEOChecks();
  }, []);

  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            {/* Global Structured Data - Organization, Website, etc. */}
            <StructuredData 
              enableOrganization={true}
              enableWebsite={true}
              enableBreadcrumbs={true}
            />
            
            <Routes>
              {/* Admin routes - outside AppLayout to avoid header/footer */}
              <Route path="/admin" element={<AdminAuth />} />
              <Route path="/admin/cx-lab" element={<AdminCXLabHome />} />
              <Route path="/admin/simulateurs" element={<AdminSimulateurs />} />
              <Route path="/admin/playbooks" element={<AdminPlaybooks />} />
              <Route path="/admin/playbooks/amazon-ads" element={<AdminPlaybookAmazonAds />} />
              <Route path="/admin/simulateurs/amz-cosmdz" element={<AdminSimulateurAmzCosmDz />} />
              <Route path="/admin/simulateurs/amz-cosmfr" element={<AdminSimulateurAmzCosmFr />} />
              <Route path="/admin/simulateurs/amz-ads" element={<AdminSimulateurAmzAds />} />
              <Route path="/admin/simulateurs/marges-physique" element={<AdminSimulateurMargesPhysique />} />
              <Route path="/fix-credentials" element={<FixCredentials />} />
              
              <Route element={<AppLayout />}>
                <Route path="/" element={<Index />} />
                <Route path="/services" element={<Services />} />
                <Route path="/services/amazon" element={<ServiceAmazonRedirect />} />
                <Route path="/accompagnement-amazon" element={<AccompagnementAmazon />} />
                <Route path="/creative-studio" element={<CreativeStudio />} />
                <Route path="/cas-clients" element={<Cases />} />
                <Route path="/methode" element={<Methode />} />
                <Route path="/audit" element={<Audit />} />
                <Route path="/audit-gratuit" element={<AuditGratuit />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                
                {/* Legacy routes */}
                <Route path="/cases" element={<Cases />} />
                <Route path="/industriels-fabricants" element={<IndustrielsFabricants />} />
                <Route path="/marques-dnvb" element={<MarquesDnvb />} />
                <Route path="/cases/amazon" element={<CasesAmazon />} />
                <Route path="/cases/amazon/volume-ventes" element={<CaseAmazonVolume />} />
                <Route path="/cases/amazon/chiffre-affaires" element={<CaseAmazonRevenue />} />
                <Route path="/cases/creative" element={<CasesCreative />} />
                <Route path="/cas/beaute-capillaire-dnvb" element={<CaseBeauteCapillaire />} />
                <Route path="/cas/food-export-premium" element={<CaseFoodExport />} />
                <Route path="/cas/tech-logistique-b2b" element={<CaseTechLogistique />} />
                <Route path="/ia" element={<IA />} />
                <Route path="/ia/amazon-ads" element={<IAAmazonAds />} />
                <Route path="/ia/seo-listings-a-plus" element={<IASeOListingsAPlus />} />
                <Route path="/ia/catalog-health-buy-box" element={<IACatalogHealthBuyBox />} />
                <Route path="/ia/prevision-demande-stocks" element={<IAPrevisionDemandeStocks />} />
                <Route path="/ia/agents-assistants-amazon" element={<IAAgentsAssistantsAmazon />} />
                <Route path="/ia/agents-assistants-amazon/impl" element={<IAAgentsAssistantsAmazonImpl />} />
                <Route path="/ia/cas-clients-ia" element={<IACasClientsIA />} />
                <Route path="/ia/audit" element={<IAAudit />} />
                <Route path="/services/buybox-sprint" element={<ServicesBuyboxSprint />} />
                <Route path="/auteurs/cosmos-wave" element={<AuteursCosmosWave />} />
                <Route path="/conformite" element={<Conformite />} />
                <Route path="/comparatifs" element={<Comparatifs />} />
                <Route path="/comparatifs/alternatives/:slug" element={<ComparatifsAlternatives />} />
                <Route path="/comparatifs/:slug-vs-cosmos-wave" element={<ComparatifsVs />} />
                <Route path="/comparatifs/top-alternatives-agences-amazon" element={<ComparatifsTopAlternatives />} />
                <Route path="/llm-facts" element={<LLMFacts />} />
                <Route path="/espace-client" element={<EspaceClient />} />
                <Route path="/save-credentials" element={<SaveCredentials />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/dashboard/:clientSlug" element={<ClientDashboard />} />
                <Route path="/dashboard/:clientSlug/settings" element={<ClientSettings />} />
                <Route path="/welcome" element={<Welcome />} />
                <Route path="/logout" element={<Logout />} />
                
                {/* Client area routes */}
                <Route path="/clients/login" element={<ClientLogin />} />
                <Route path="/clients" element={<Navigate to="/espace-client" replace />} />
                
                {/* DEPRECATED - kept for SEO compatibility */}
                <Route path="/services/amazon-direct" element={<ServicesAmazon />} />
                
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            <WhatsAppChat />
            <ConsentManager />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
};

export default App;
